import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from 'store/ducks';

import { ReportDashItemWithCalendar } from 'components/ReportDashItem/ReportDashboardItemWithCalendar';
import { eventName, relatoriosAction } from 'components/ApplicationInsights/eventNames';
import { ReportDashItem } from 'components/ReportDashItem';
import TabNavigation from 'components/TabNavigation';
import Text from 'components/Text';

import ReportService from 'services/report.service';
import TicketService from 'services/ticket.service';

import { Period } from 'utils/Date/interface';
import { DownloadConsolidatedProtocols } from './interface';
import { ReportDashDataIntegration } from 'components/ReportDashDataIntegration';

const UsersReport: React.FC = () => {
  const user = useSelector((state: RootState) => state.user);
  const navigate = useNavigate();

  const _reportServices = new ReportService();
  const _ticketServices = new TicketService();

  const [usersFilter, setUsersFilter] = useState({
    userType: { eq: 'Employee' },
  });

  useEffect(() => {
    if (user?.userType) {
      if (
        (user?.userType === 'employee' && user.roles?.[0] === 'administrator')
      ) {
        return;
      }
      navigate('/dashboard');
    }
  }, [user]);

  const exportUsers = useCallback(async () => {
    await _reportServices.DownloadReportUser('export-user-excel', { ...usersFilter });
  }, [usersFilter]);

  const exportLegalDocuments = async () => {
    await _reportServices.DownloadReportLegalDocuments();
  };

  const exportQualifications = async (period: Period) => {
    const sentObj = {
      startPeriod: new Date(period.from).toISOString(),
      lastPeriod: new Date(period.to).toISOString()
    };
    await _reportServices.DownloadQualifications(sentObj);
  };

  const exportConsolidatedProtocols = async () => {
    await _ticketServices.downloadData(DownloadConsolidatedProtocols);
  };

  const exportNpsPhysicalStore = async (period: Period) => {
    const sentObj = {
      startPeriod: new Date(period.from).toISOString(),
      lastPeriod: new Date(period.to).toISOString()
    };

    await _reportServices.DownloadNpsPhysicalStores(sentObj);
  };

  return (
    <>
      <Text size={24} className="color-primary font-bold">
        Relatórios
      </Text>
      <TabNavigation
        trackingKey="Abas Relatório de Usuários"
        className="mt-3"
        setTabFilter={(filter) => setUsersFilter(filter)}
        tabLabels={[
          { label: 'Usuários internos CCare', filter: { userType: { eq: 'Employee' }, }, trackingName: relatoriosAction.ABA_USUARIOS_INTERNOS },
          { label: 'Usuários de clientes', filter: { userType: { eq: 'Customer' } }, trackingName: relatoriosAction.ABA_USUARIOS_CLIENTES },
          { label: 'Documentação Legal', filter: { userType: { eq: 'LegalDocuments' } }, trackingName: relatoriosAction.ABA_RELATORIO_DOCUMENTOS_LEGAIS },
          { label: 'Qualificações', trackingName: relatoriosAction.ABA_RELATORIO_QUALIFICACOES },
          { label: 'Protocolos Consolidados', trackingName: relatoriosAction.ABA_RELATORIO_PROTOCOLOS_CONSOLIDADOS },
          { label: 'NPS Lojas Físicas', trackingName: relatoriosAction.ABA_RELATORIO_NPS_LOJAS_FISICAS },
          { label: 'Integração de Dados', trackingName: relatoriosAction.ABA_RELATORIO_NPS_LOJAS_FISICAS },
        ]}
        pages={[
          <ReportDashItem
            description={'Exportar a base de usuários internos do portal Customer Care.'}
            trackName={relatoriosAction.RELATORIO_USUARIOS_INTERNOS}
            trackItem={eventName.DOWNLOAD_REPORTS_USERS}
            action={exportUsers} />,
          <ReportDashItem
            description={'Exportar a base de usuários dos clientes cadastrados no portal Customer Care.'}
            trackName={relatoriosAction.RELATORIO_USUARIOS_CLIENTES}
            trackItem={eventName.DOWNLOAD_REPORTS_USERS}
            action={exportUsers} />,
          <ReportDashItem
            description={'Exportar relatório de status de documentação legal dos clientes.'}
            trackName={relatoriosAction.RELATORIO_DOCUMENTOS_LEGAIS}
            trackItem={eventName.DOWNLOAD_REPORTS_LEGAL_DOCUMENTS}
            action={exportLegalDocuments} />,
          <ReportDashItemWithCalendar
            description={'Exportar relatório de qualificações.'}
            trackName={relatoriosAction.RELATORIO_QUALIFICACOES}
            trackItem={eventName.DOWNLOAD_REPORTS_QUALIFICATIONS}
            action={exportQualifications} />,
          <ReportDashItem
            description={'Exportar relatório de protocolos consolidados.'}
            trackName={relatoriosAction.RELATORIO_PROTOCOLOS_CONSOLIDADOS}
            trackItem={eventName.DOWNLOAD_REPORTS_CONSOLIDATED_PROTOCOLS}
            action={exportConsolidatedProtocols} />,
          <ReportDashItemWithCalendar
            description={'Exportar relatório de NPS Lojas Físicas.'}
            trackName={relatoriosAction.ABA_RELATORIO_NPS_LOJAS_FISICAS}
            trackItem={eventName.DOWNLOAD_REPORTS_NPS_LOJAS_FISICAS}
            action={exportNpsPhysicalStore} />,
          <ReportDashDataIntegration />
        ]}
      />
    </>
  );
};

export default UsersReport;
